/**
 * useFinancialContext Hook
 * 
 * Aggregates all financial data from Redux store into a comprehensive context
 * that can be injected into AI prompts for the financial advisor chat.
 */

import { useMemo } from 'react'
import { useAppSelector } from '@/store/hooks'
import type { ChatFinancialContext } from '@/types'
import { generateWealthProjection, calculateRetirementReadiness, calculateRetirementYearsFunded } from '@/services/projectionEngine'

export function useFinancialContext(): ChatFinancialContext {
  const lifePlan = useAppSelector((state) => state.lifePlan)
  const timeline = useAppSelector((state) => state.timeline)
  
  return useMemo(() => {
    const currentYear = new Date().getFullYear()
    
    // Calculate age
    const birthYear = lifePlan.personal.birthDate 
      ? new Date(lifePlan.personal.birthDate).getFullYear() 
      : currentYear - 30
    const age = currentYear - birthYear
    
    // Calculate years to retirement
    const yearsToRetirement = Math.max(0, lifePlan.personal.retirementAge - age)
    
    // Generate projections
    const projection = generateWealthProjection({ lifePlan, timeline })
    const retirementReadiness = calculateRetirementReadiness(projection, lifePlan)
    const retirementYearsFunded = calculateRetirementYearsFunded(projection, lifePlan)
    
    // Calculate asset breakdown
    const cashAndChecking = lifePlan.assets
      .filter(a => ['cash', 'checking', 'savings', 'money_market'].includes(a.category))
      .reduce((sum, a) => sum + a.currentValue, 0)
    
    const investments = lifePlan.assets
      .filter(a => a.category === 'brokerage')
      .reduce((sum, a) => sum + a.currentValue, 0)
    
    const retirement401k = lifePlan.assets
      .filter(a => ['401k', '403b'].includes(a.category))
      .reduce((sum, a) => sum + a.currentValue, 0)
    
    const retirementIRA = lifePlan.assets
      .filter(a => ['ira_traditional', 'ira_roth'].includes(a.category))
      .reduce((sum, a) => sum + a.currentValue, 0)
    
    const realEstateEquity = lifePlan.assets
      .filter(a => a.category === 'real_estate')
      .reduce((sum, a) => sum + a.currentValue, 0)
    
    const otherAssets = lifePlan.assets
      .filter(a => !['cash', 'checking', 'savings', 'money_market', 'brokerage', '401k', '403b', 'ira_traditional', 'ira_roth', 'real_estate'].includes(a.category))
      .reduce((sum, a) => sum + a.currentValue, 0)
    
    // Calculate debt breakdown
    const mortgage = lifePlan.debts
      .filter(d => d.category === 'mortgage')
      .reduce((sum, d) => sum + d.balance, 0)
    
    const studentLoans = lifePlan.debts
      .filter(d => ['student_federal', 'student_private'].includes(d.category))
      .reduce((sum, d) => sum + d.balance, 0)
    
    const creditCards = lifePlan.debts
      .filter(d => d.category === 'credit_card')
      .reduce((sum, d) => sum + d.balance, 0)
    
    const autoLoans = lifePlan.debts
      .filter(d => d.category === 'auto')
      .reduce((sum, d) => sum + d.balance, 0)
    
    const otherDebts = lifePlan.debts
      .filter(d => !['mortgage', 'student_federal', 'student_private', 'credit_card', 'auto'].includes(d.category))
      .reduce((sum, d) => sum + d.balance, 0)
    
    const totalDebtPayments = lifePlan.debts.reduce(
      (sum, d) => sum + d.minimumPayment + d.extraPayment, 0
    )
    
    const highestInterestRate = lifePlan.debts.reduce(
      (max, d) => Math.max(max, d.interestRate), 0
    )
    
    // Calculate DTI
    const monthlyIncome = lifePlan.computed.monthlyIncome
    const debtToIncomeRatio = monthlyIncome > 0 
      ? (totalDebtPayments / monthlyIncome) * 100 
      : 0
    
    // Savings rate
    const monthlySavings = monthlyIncome - lifePlan.computed.monthlyExpenses
    const savingsRate = monthlyIncome > 0 
      ? (Math.max(0, monthlySavings) / monthlyIncome) * 100 
      : 0
    
    // Emergency fund calculation
    const liquidAssets = cashAndChecking + investments
    const monthlyExpenses = lifePlan.computed.monthlyExpenses || 5000
    const emergencyFundMonths = monthlyExpenses > 0 
      ? liquidAssets / monthlyExpenses 
      : 0
    
    // Get children ages
    const childrenAges = lifePlan.family.children.map(child => {
      const childBirthYear = new Date(child.birthDate).getFullYear()
      return currentYear - childBirthYear
    })
    
    // Get spouse age
    const spouseAge = lifePlan.family.spouse?.birthDate
      ? currentYear - new Date(lifePlan.family.spouse.birthDate).getFullYear()
      : undefined
    
    // Get upcoming events
    const upcomingEvents = timeline.events
      .filter(e => new Date(e.date) > new Date())
      .slice(0, 5)
      .map(e => ({
        name: e.name,
        type: e.type,
        date: e.date,
        financialImpact: (e.financialImpact.oneTimeIncome || 0) - (e.financialImpact.oneTimeExpense || 0) + 
          (e.financialImpact.incomeChange || 0) - (e.financialImpact.expenseChange || 0),
      }))
    
    // Get current phase
    const currentPhase = timeline.phases.find(p => p.isActive)?.name || 'Not set'
    
    // Get next milestone
    const nextMilestone = timeline.events
      .filter(e => new Date(e.date) > new Date())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0]?.name
    
    // Get top goals
    const topGoals = lifePlan.goals
      .sort((a, b) => {
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 }
        return priorityOrder[a.priority] - priorityOrder[b.priority]
      })
      .slice(0, 3)
      .map(g => ({
        name: g.name,
        targetAmount: g.targetAmount,
        currentProgress: g.targetAmount > 0 ? (g.currentAmount / g.targetAmount) * 100 : 0,
        targetDate: g.targetDate,
        yearsAway: Math.ceil((new Date(g.targetDate).getTime() - Date.now()) / (365.25 * 24 * 60 * 60 * 1000)),
      }))
    
    // Retirement projections
    const retirementYear = birthYear + lifePlan.personal.retirementAge
    const retirementProjection = projection.points.find(p => p.year === retirementYear)
    const projectedRetirementIncome = (lifePlan.preferences.expectedSocialSecurity * 12) +
      (lifePlan.preferences.hasPension ? lifePlan.preferences.pensionMonthly * 12 : 0) +
      ((retirementProjection?.netWorth || 0) * 0.04) // 4% withdrawal
    
    // Risk analysis
    const hasEmergencyFund = emergencyFundMonths >= 3
    const isUnderinsured = !lifePlan.insurance.life.length && lifePlan.family.children.length > 0
    const highDebtBurden = debtToIncomeRatio > 40
    const onTrackForRetirement = retirementReadiness >= 80
    
    let biggestRisk = 'None identified'
    if (!hasEmergencyFund) biggestRisk = 'Insufficient emergency fund'
    else if (highDebtBurden) biggestRisk = 'High debt-to-income ratio'
    else if (isUnderinsured) biggestRisk = 'Insufficient life insurance for dependents'
    else if (!onTrackForRetirement) biggestRisk = 'Behind on retirement savings'
    
    const context: ChatFinancialContext = {
      personal: {
        age,
        retirementAge: lifePlan.personal.retirementAge,
        yearsToRetirement,
        lifeExpectancy: lifePlan.personal.lifeExpectancy,
        location: lifePlan.personal.state || 'Unknown',
        filingStatus: lifePlan.personal.filingStatus,
      },
      family: {
        maritalStatus: lifePlan.family.maritalStatus,
        hasSpouse: !!lifePlan.family.spouse,
        spouseAge,
        spouseIncome: lifePlan.family.spouse?.currentIncome,
        numberOfChildren: lifePlan.family.children.length,
        childrenAges,
        plannedChildren: lifePlan.family.plannedChildren.length,
        hasDependents: lifePlan.family.dependents.length > 0,
      },
      career: {
        employmentStatus: lifePlan.career.employmentStatus,
        industry: lifePlan.career.currentJob?.industry || 'Unknown',
        jobTitle: lifePlan.career.currentJob?.title || 'Unknown',
        yearsExperience: lifePlan.career.currentJob?.yearsExperience || 0,
        currentSalary: lifePlan.career.currentJob?.baseSalary || 0,
        totalCompensation: lifePlan.career.currentJob
          ? lifePlan.career.currentJob.baseSalary + 
            lifePlan.career.currentJob.bonus + 
            lifePlan.career.currentJob.commission +
            lifePlan.career.currentJob.stockCompensation
          : 0,
        hasSideIncome: lifePlan.career.sideIncomes.length > 0,
        sideIncomeTotal: lifePlan.career.sideIncomes.reduce((sum, s) => sum + s.annualAmount, 0),
      },
      finances: {
        netWorth: lifePlan.computed.netWorth,
        totalAssets: lifePlan.computed.totalAssets,
        totalDebts: lifePlan.computed.totalDebts,
        liquidAssets,
        retirementAssets: retirement401k + retirementIRA,
        monthlyIncome,
        monthlyExpenses,
        monthlySavings,
        savingsRate: Math.round(savingsRate),
        emergencyFundMonths: Math.round(emergencyFundMonths * 10) / 10,
      },
      assets: {
        cash: cashAndChecking,
        investments,
        retirement401k,
        retirementIRA,
        realEstateEquity,
        otherAssets,
      },
      debts: {
        mortgage,
        studentLoans,
        creditCards,
        autoLoans,
        otherDebts,
        totalMonthlyPayments: totalDebtPayments,
        highestInterestRate,
        debtToIncomeRatio: Math.round(debtToIncomeRatio),
      },
      insurance: {
        hasHealthInsurance: lifePlan.insurance.health.type !== 'none',
        healthInsuranceType: lifePlan.insurance.health.type,
        hasLifeInsurance: lifePlan.insurance.life.length > 0,
        lifeInsuranceCoverage: lifePlan.insurance.life.reduce((sum, l) => sum + l.coverageAmount, 0),
        hasDisabilityInsurance: lifePlan.insurance.disability.hasLongTerm || lifePlan.insurance.disability.hasShortTerm,
        disabilityCoverage: lifePlan.insurance.disability.longTermCoverage,
      },
      goals: {
        totalGoals: lifePlan.goals.length,
        topGoals,
      },
      timeline: {
        upcomingEvents,
        currentPhase,
        nextMilestone,
      },
      projections: {
        wealthAtRetirement: retirementProjection?.netWorth || 0,
        retirementReadinessScore: retirementReadiness,
        socialSecurityEstimate: lifePlan.preferences.expectedSocialSecurity,
        projectedRetirementIncome: Math.round(projectedRetirementIncome),
        yearsOfRetirementFunded: retirementYearsFunded,
      },
      risks: {
        hasEmergencyFund,
        isUnderinsured,
        highDebtBurden,
        onTrackForRetirement,
        biggestRisk,
      },
    }
    
    return context
  }, [lifePlan, timeline])
}

/**
 * Format financial context as a string for AI prompt injection
 */
export function formatContextForPrompt(context: ChatFinancialContext): string {
  return JSON.stringify(context, null, 2)
}
